import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Stack, Button, IconButton, TextField, Chip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox,
  CircularProgress, Alert, Tooltip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import { ENDPOINTS, apiCall } from '../utils/api.js';
import ShareButton from './ShareButton.js';

const catLabel = (c) => c.name || c.description || `Category #${c.id}`;

// Dialog to pick which of the org's categories belong to a facade.
function AssignDialog({ open, facade, categories, onClose, onSaved }) {
  const [checked, setChecked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !facade) return undefined;
    // Defer to a macrotask so we don't call setState synchronously inside the effect body.
    const t = setTimeout(() => setChecked(new Set(facade.category_ids || [])), 0);
    return () => clearTimeout(t);
  }, [open, facade]);

  const toggle = (id) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const save = async () => {
    setBusy(true); setError('');
    try {
      await apiCall(ENDPOINTS.FACADE_SET_CATEGORIES(facade.id), {
        method: 'POST', body: JSON.stringify({ category_ids: Array.from(checked) }),
      });
      onSaved();
    } catch (e) { setError(e.message || 'Failed to save.'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Queues in “{facade?.name}”</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {categories.length === 0 ? (
          <Typography variant="body2" color="text.secondary">This organization has no categories yet.</Typography>
        ) : (
          <Stack>
            {categories.map((c) => (
              <FormControlLabel
                key={c.id}
                control={<Checkbox size="small" checked={checked.has(c.id)} onChange={() => toggle(c.id)} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{catLabel(c)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.type || 'general'}{c.is_scheduled ? ' · scheduled' : ''}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy}>
          {busy ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Org-admin management of category facades (display-only groupings). Self-contained: fetches
 * the org's facades, lets the admin create / rename / delete them and assign categories.
 * `categories` are the org's categories (passed in so we don't refetch); `onChange` lets the
 * parent refresh if it also renders facades.
 */
export default function FacadeManager({ orgId, orgSlug, categories = [], onChange }) {
  const [facades, setFacades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [assignFor, setAssignFor] = useState(null);
  const [renameFor, setRenameFor] = useState(null);
  const [renameText, setRenameText] = useState('');
  const [deleteFor, setDeleteFor] = useState(null);

  const catById = new Map(categories.map((c) => [c.id, c]));

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    const d = await apiCall(ENDPOINTS.FACADES_BY_ORG(orgId)).catch(() => ({ results: [] }));
    setFacades(d.results || d || []);
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    let alive = true;
    (async () => { if (alive) await load(); })();
    return () => { alive = false; };
  }, [load]);

  const refresh = async () => { await load(); if (onChange) onChange(); };

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true); setError('');
    try {
      await apiCall(ENDPOINTS.FACADES, {
        method: 'POST', body: JSON.stringify({ organization: Number(orgId), name }),
      });
      setNewName('');
      await refresh();
    } catch (e) { setError(e.message || 'Failed to create group.'); }
    setBusy(false);
  };

  const rename = async () => {
    const name = renameText.trim();
    if (!name) return;
    setBusy(true);
    try {
      await apiCall(ENDPOINTS.FACADE_DETAIL(renameFor.id), {
        method: 'PATCH', body: JSON.stringify({ name }),
      });
      setRenameFor(null);
      await refresh();
    } catch (e) { setError(e.message || 'Failed to rename.'); }
    setBusy(false);
  };

  const remove = async () => {
    setBusy(true);
    try {
      await apiCall(ENDPOINTS.FACADE_DETAIL(deleteFor.id), { method: 'DELETE' });
      setDeleteFor(null);
      await refresh();
    } catch (e) { setError(e.message || 'Failed to delete.'); }
    setBusy(false);
  };

  return (
    <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <LayersOutlinedIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" fontWeight={700}>Groups (facades)</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Optional: nest related queues under one tile (e.g. “Dr. A” → walk-in + scheduled).
        Customers see the group first, then pick a queue. Ungrouped queues show as usual.
      </Typography>

      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, mb: 1.5 }}>
        <TextField
          size="small" fullWidth placeholder="New group name (e.g. Dr. A)"
          value={newName} onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
        />
        <Button variant="contained" onClick={create} disabled={busy || !newName.trim()} sx={{ borderRadius: 6, flexShrink: 0 }}>
          Add
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
      ) : facades.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No groups yet — queues display in a flat list.
        </Typography>
      ) : (
        <Stack divider={<Divider flexItem />} spacing={0}>
          {facades.map((f) => {
            const kids = (f.category_ids || []).map((id) => catById.get(id)).filter(Boolean);
            return (
              <Box key={f.id} sx={{ py: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700}>{f.name}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {kids.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">No queues assigned</Typography>
                    ) : kids.map((c) => (
                      <Chip key={c.id} label={catLabel(c)} size="small" variant="outlined" sx={{ height: 22 }} />
                    ))}
                  </Box>
                </Box>
                <Button size="small" onClick={() => setAssignFor(f)}>Assign</Button>
                <ShareButton
                  url={`${window.location.origin}/org/${orgSlug}/facade/${f.slug}`}
                  title={f.name}
                  text={`Join the "${f.name}" queue on sqip`}
                  tooltip="Share group link"
                />
                <Tooltip title="Rename">
                  <IconButton size="small" onClick={() => { setRenameFor(f); setRenameText(f.name); }}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete group (queues are kept)">
                  <IconButton size="small" color="error" onClick={() => setDeleteFor(f)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Stack>
      )}

      <AssignDialog
        open={!!assignFor} facade={assignFor} categories={categories}
        onClose={() => setAssignFor(null)}
        onSaved={() => { setAssignFor(null); refresh(); }}
      />

      <Dialog open={!!renameFor} onClose={() => setRenameFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename group</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" sx={{ mt: 1 }} value={renameText}
            onChange={(e) => setRenameText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') rename(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameFor(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={rename} disabled={busy || !renameText.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteFor} onClose={() => setDeleteFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete “{deleteFor?.name}”?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            The group is removed and its queues become ungrouped. No queues or appointments are deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFor(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={remove} disabled={busy}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
